/**
 * ValidationView — the result of the last check-only deploy against the org (R3
 * `TaskSnapshot.validation`).
 *
 * `CheckView.failures` is `component_key → messages`, where `messages` can arrive as either an array
 * or a single string (`read_routes.py`'s comment: "as the `check_finished` frame carries it") — both
 * are rendered as a list so a caller never has to branch on the shape.
 */
import { AlertTriangle, Check, HelpCircle, XCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import type { TaskSnapshot } from "@/services/multi-agent/laneV2/types";
import { CHECK_VERDICT_LABEL, checkErrorSummary, checkOriginLabel, checksNewestFirst, checkVerdict, formatLocalTimestamp, testLevelWords } from "./views";

export interface ValidationViewProps {
  validation: TaskSnapshot["validation"];
  className?: string;
}

/** When the last check finished, in the reader's locale; null when the wire carries no time. */
function checkTime(check: NonNullable<NonNullable<TaskSnapshot["validation"]>["last_check"]>): string | null {
  const at = check.finished_at ?? check.completed_date ?? check.created_at ?? null;
  return at ? formatLocalTimestamp(at) : null;
}

export function ValidationView({ validation, className }: ValidationViewProps) {
  const check = validation?.last_check ?? null;

  return (
    <section aria-labelledby="lanev2-validation-title" className={cn("rounded-md border border-border bg-card", className)}>
      <header className="px-3.5 py-2.5">
        <h3 id="lanev2-validation-title" className="text-[13px] font-semibold text-foreground">
          Check against your org
        </h3>
      </header>

      {!validation || !check ? (
        <p className="border-t border-border px-3.5 py-3 text-[12px] text-muted-foreground">Not checked against the org yet.</p>
      ) : (
        <div className="border-t border-border px-3.5 py-3">
          {/* `ok` is `null` when the org has never reported a result for this deploy_id at all --
              distinct from a completed check that failed, and never rendered as "Failed". */}
          <div
            className={cn(
              "flex items-center gap-1.5 text-[13px] font-medium",
              check.ok === null ? "text-muted-foreground" : check.ok ? "text-ma-ok-text" : "text-ma-err-text",
            )}
          >
            {check.ok === null ? (
              <HelpCircle className="h-4 w-4 shrink-0" aria-hidden />
            ) : check.ok ? (
              <Check className="h-4 w-4 shrink-0" aria-hidden />
            ) : (
              <XCircle className="h-4 w-4 shrink-0" aria-hidden />
            )}
            {CHECK_VERDICT_LABEL[checkVerdict(check)]}
            {checkTime(check) && (
              <span data-lane-v2-check-time="" className="font-normal text-muted-foreground">
                {" "}
                · {checkTime(check)}
              </span>
            )}
          </div>

          <details data-lane-v2-validation-details="" className="mt-1.5">
            <summary className="cursor-pointer text-[11px] font-medium text-muted-foreground hover:text-foreground">Details</summary>
            <dl className="mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground sm:grid-cols-4">
              {validation.validation_id && (
                <div>
                  <dt className="uppercase tracking-wide">Check id</dt>
                  <dd className="font-mono text-foreground">{validation.validation_id}</dd>
                </div>
              )}
              {check.test_level && (
                <div>
                  <dt className="uppercase tracking-wide">Tests run</dt>
                  <dd className="text-foreground">{testLevelWords(check.test_level)}</dd>
                </div>
              )}
              {check.completed_date && (
                <div>
                  <dt className="uppercase tracking-wide">Completed</dt>
                  <dd className="text-foreground">{check.completed_date}</dd>
                </div>
              )}
              {check.run_tests && check.run_tests.length > 0 && (
                <div>
                  <dt className="uppercase tracking-wide">Tests run</dt>
                  <dd className="text-foreground">{check.run_tests.length}</dd>
                </div>
              )}
            </dl>
          </details>

          {check.detail && (
            <p className="mt-2 flex items-start gap-1.5 text-[12px] text-foreground">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
              {check.detail}
            </p>
          )}

          {check.failures && Object.keys(check.failures).length > 0 && (
            <div className="mt-2">
              <h4 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                {Object.keys(check.failures).length} component {Object.keys(check.failures).length === 1 ? "failure" : "failures"}
              </h4>
              <ul className="mt-1 space-y-1.5">
                {Object.entries(check.failures).map(([component, messages]) => {
                  const list = Array.isArray(messages) ? messages : [messages];
                  return (
                    <li key={component} className="text-[12px]">
                      <span className="font-mono text-foreground">{component}</span>
                      <ul className="mt-0.5 list-disc space-y-0.5 pl-4 text-ma-err-text">
                        {list.map((msg, i) => (
                          <li key={i}>{msg}</li>
                        ))}
                      </ul>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      )}

      {validation?.checks && validation.checks.length > 0 && <CheckHistory checks={validation.checks} />}
    </section>
  );
}

/** Every check this task has had, newest first: verdict, when, what started it, and why it failed. Bounded by the server (20). */
export function CheckHistory({ checks }: { checks: NonNullable<NonNullable<TaskSnapshot["validation"]>["checks"]> }) {
  const rows = checksNewestFirst(checks);
  return (
    <div data-lane-v2-check-history="" className="border-t border-border px-3.5 py-3">
      <h4 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        History ({rows.length})
      </h4>
      <ul className="mt-1.5 space-y-1.5">
        {rows.map((c, i) => {
          const verdict = checkVerdict(c);
          const when = c.finished_at ?? c.created_at;
          const summary = checkErrorSummary(c);
          return (
            <li key={c.deploy_id ?? i} data-check-verdict={verdict} className="text-[12px] leading-snug">
              <span
                className={cn(
                  "font-medium",
                  verdict === "passed" ? "text-ma-ok-text" : verdict === "failed" ? "text-ma-err-text" : "text-muted-foreground",
                )}
              >
                {CHECK_VERDICT_LABEL[verdict]}
              </span>
              <span className="text-muted-foreground">
                {when ? ` · ${formatLocalTimestamp(when)}` : ""} · {checkOriginLabel(c.origin)}
              </span>
              {summary && <span className="block text-[11px] text-ma-err-text">{summary}</span>}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
